import { CommonDialogs } from "@mcleod/common";
import {
    Checkbox, ClickEvent, Component, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, DropdownItem,
    DropdownSelectionEvent, Label, SelectionMode, TableRow, TableRowDisplayEvent, Textbox, ValidationResult
} from "@mcleod/components";
import { DuplicateId, DuplicateIdProps } from "../DuplicateId";
import { AutogenLayoutPnnLaneBuilderProfile } from "./autogen/AutogenLayoutPnnLaneBuilderProfile";
import { Model, ModelRow, ModelSearchResult, StringUtil } from "@mcleod/core";
import { PnnLaneBuilderProfileList } from "./PnnLaneBuilderProfileList";

const viewBuilderProfileURL = "lme/dispatch/pnn/PnnLaneBuilderProfile?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "pnn_builder_profile",
    screenName: "PNN Builder Profile",
    navigateToUrl: viewBuilderProfileURL
};

export class PnnLaneBuilderProfile extends AutogenLayoutPnnLaneBuilderProfile {
    override onLoad(): void | Promise<void> {
        this.syncControls();
        this.textboxExcludeEmailDomains.addlValidationCallback = (value: string, mode: DataSourceMode): ValidationResult => {
            return this.emailDomainValidator(value, mode);
        };

        this.sourcePnnBuilderProfile.doBeforeDataSourcePost = async () => {
            const recurringDate = this.activeRow?.get("recurring_date");
            const dayIsChecked = this.checkboxRunSunday.checked || this.checkboxRunMonday.checked || this.checkboxRunTuesday.checked || this.checkboxRunWednesday.checked || this.checkboxRunThursday.checked || this.checkboxRunFriday.checked || this.checkboxRunSaturday.checked;
            const result = !(recurringDate === "D" && !dayIsChecked);
            if (!result) {
                CommonDialogs.showError("Invalid recurring date. Please select at least one day of the week.").then(() => this.checkboxRunSunday.focus());
            }
            return result;
        }

        this.configureProcessLogInitial();
        DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
    }

    /** This is an event handler for the afterExecution event of sourcePnnBuilderProfile. */
    sourcePnnBuilderProfileAfterExecution(event: DataSourceExecutionEvent) {
        this.syncControls();
    }

    syncControls() {
        this.syncDateControls();
        this.syncScheduleFieldRequirement();
        this.syncContactFieldRequirement();
    }

    syncDateControls() {
        const recurringDate = this.activeRow?.get("recurring_date");
        this.panelDaily.getRecursiveChildren().forEach(child => this.syncDateControl(child, recurringDate === "D"));
        this.panelWeekdayMonth.getRecursiveChildren().forEach(child => this.syncDateControl(child, recurringDate === "W"));
        this.panelDayMonth.getRecursiveChildren().forEach(child => this.syncDateControl(child, recurringDate === "M"));

        this.textboxMonthlyDate.required = recurringDate === "M";
        this.textboxMonthFrequency.required = recurringDate === "W";
        this.textboxMonthDay.required = recurringDate === "W";
    }

    syncScheduleFieldRequirement() {
        const scheduleOption = this.activeRow?.get("schedule_option");
        this.textboxDefaultSchedId.required = !!scheduleOption;
    }

    syncContactFieldRequirement() {
        const orderReplyTo = this.sourcePnnControl?.activeRow?.get("order_contact")
        this.textboxContactOption.required = orderReplyTo === "P"; //checking if order reply to field is set to PNN Carrier
    }

    /** This is an event handler for the afterDropdownSelection event of textboxRecurringDate. */
    textboxRecurringDateAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.syncDateControls();
    }

    /** This is an event handler for the afterDropdownSelection event of textboxScheduleOption. */
    textboxScheduleOptionAfterDropdownSelection(event: DropdownSelectionEvent) {
        this.syncScheduleFieldRequirement();
    }

    private emailDomainValidator(value: string, mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (value) {
            const domains = value.split(",");
            for (const domain of domains) {
                if (!/^[a-zA-Z0-9_-]+\.[a-z]+$/.test(domain)) {
                    return {
                        component: this.textboxExcludeEmailDomains,
                        isValid: false,
                        validationMessage: "Invalid domain format. Email domains must be in the following format [abc.com,xyz.com]"
                    };
                }
            }
        }
        return null;
    }

    syncDateControl(component: Component, visible: boolean) {
        component.visible = visible;
        if(!visible) {
            if(component instanceof Textbox) {
                component.clear();
            }
            else if (component instanceof Checkbox) {
                component.checked = false;
            }
        }
    }

    sourcePnnBuilderProfileAfterModeChange(event: DataSourceModeChangeEvent) {
        this.syncHistoryTab(event.newMode);
    }

    private syncHistoryTab(mode: DataSourceMode) {
        if(mode !== DataSourceMode.UPDATE) {
            this.tabHistory.visible = false;
        }
        else {
            this.tabHistory.visible = true;
            this.configureProcessLogForProfile();
        }
    }

    /**
     * Perform initial setup for the process log layout (History tab)
     */
    private configureProcessLogInitial() {
        this.layoutProcessLog.addLayoutLoadListener(() => {
            this.layoutProcessLog.tableProcessLog.removeColumn("columnHeaderE");
            this.layoutProcessLog.tableProcessLog.removeColumn("columnHeaderD");
            this.layoutProcessLog.textboxDateRun.dateDefault = "t-90,t";
            this.layoutProcessLog.textboxDescr.visible = false;

            // Convert process type metadata to format specific to lane builder
            this.layoutProcessLog.textboxProcessType.items = [
                new DropdownItem("P", "Manual"),
                new DropdownItem("S", "Automated")
            ];
            this.layoutProcessLog.tableProcessLog.selectionMode = SelectionMode.SINGLE;
            this.layoutProcessLog.tableProcessLog.addRowDisplayListener((event: TableRowDisplayEvent) => {
                const tableRow = event.getTableRow();
                const labelProcessType = tableRow?.findComponentById("labelProcessType") as Label;
                const processType = tableRow?.data?.get("process_type");
                if(processType === "P") {
                    labelProcessType.text = "Manual";
                }
                else if(processType === "S") {
                    labelProcessType.text = "Automated";
                }
                tableRow.addDblClickListener((event: ClickEvent) => {
                    const tableRow = event.target as TableRow;
                    const modelRow = tableRow?.data as ModelRow;
                    Model.search("lme/powerbroker/pnn-builder-history-dtl", {process_log_id: modelRow.get("id")}).then(result => {
                        const modelRows = result.modelRows;
                        const summaryData = result.summaryData;
                        PnnLaneBuilderProfileList.showResultsSlideout(modelRows, summaryData, null, true);
                    });
                });
            });
        });
    }

    /**
     * Query for process log records specific to the displayed lane builder profile
     */
    private configureProcessLogForProfile() {
        // Make search only show the current builder profile's process
        const profileId = this.activeRow?.get("id");
        if (!StringUtil.isEmptyString(profileId)) {
            const descrItem = new DropdownItem("PNN builder process - " + profileId);
            this.layoutProcessLog.textboxDescr.items = [ descrItem ];
            this.layoutProcessLog.textboxDescr.selectedItem = descrItem;
            this.layoutProcessLog.tableProcessLog.dataSource.search({ descr: descrItem.value });
        }
    }
}
